import React, { useEffect, useState, useRef } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import useSnack from 'hooks/useSnack';
import { API } from 'aws-amplify';
import StudentInfoForm from './StudentInfoForm';
import ProductSelection from './ProductSelection';
import Payment from './Payment';
import Agreement from './Agreement';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  actions: {
    'justifyContent': 'center',
    'boxShadow': '-24px -24px 40px -20px rgba(0, 0, 0, 0.03)',
    'padding': theme.spacing(2),
    '& button': {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
}));

const Purchase = ({ history }) => {
  const classes = useStyles();
  const [values, setValues] = useState({});
  const [customer, setCustomer] = useState();
  const [step, setStep] = useState(1);
  const snack = useSnack();
  const formRef = useRef(null);

  useEffect(() => {
    API.get('api', `/stripe/customer`).then(
      (resp) => {
        console.log(resp);
        setCustomer(resp);
      },
      (e) => {
        console.log(e);
        snack.error('Error loading card details');
      },
    );
  }, []);

  const onFieldChange = (field, value) => {
    setValues({
      ...values,
      [field]: value,
    });
  };

  const onUpdateStudent = (data) => {
    setValues({
      ...values,
      ...data,
    });
    setStep(4);
  };

  const onSubmitPayment = (token) => {
    if (!token && !customer) {
      return snack.error('Missing payment token or customer');
    }
    return API.post('api', '/users/add_student', {
      body: {
        ...values,
        planId: values.product.plan_id,
        token,
      },
    }).then(
      (resp) => {
        setStep(6);
      },
      (err) => {
        if (err.response?.data) {
          snack.error(err.response.data.message);
        } else {
          snack.error(err.message);
        }
      },
    );
  };

  const onNext = async () => {
    switch (step) {
      case 1: {
        if (values.year) {
          setStep(step+1);
        }
        break;
      }
      case 2: {
        if(values.product) {
          setStep(step+1);
        }
        break;
      }
      case 3: {
        if(values.tos) {
          setStep(step+1);
        }
        break;
      }
      case 4: {
        formRef.current.validateFields().then(
          (resp) => {
            setValues({...values, ...resp });
            setStep(step+1);
          },
          (e) => snack.error('Please validate inputs')
        )
        break;
      }
    }
  }

  return (
    <Box>
      <Helmet title="Subscribe" />
      <Grid justify="center" container>
        <Grid item md={8}>
          <Card>
            <CardContent>
              <Box marginBottom={4}>
                <Typography component="div" align="center" variant="caption">
                  Add Student - Step {step}/6
                </Typography>
              </Box>
              <Box paddingBottom={4}>
                {step <= 2 && (
                  <ProductSelection
                    year={values.year}
                    product={values.product}
                    onChange={onFieldChange}
                    step={step}
                  />
                )}
                {step === 3 && (
                  <Agreement
                    tos={values.tos}
                    product={values.product}
                    onChange={onFieldChange}
                  />
                )}
                {step === 4 && (
                  <StudentInfoForm
                    ref={formRef}
                    values={values}
                    onSubmit={onUpdateStudent}
                    onBack={() => setStep(1)}
                  />
                )}
                {step === 5 && (
                  <Payment
                    customer={customer}
                    onChange={setValues}
                    onSubmit={onSubmitPayment}
                    data={values}
                    product={values.product}
                    setStep={setStep}
                  />
                )}
                {step === 6 && (
                  <Box textAlign="center" marginTop={4}>
                    <Typography gutterBottom variant="h5">
                      Thank you!
                    </Typography>
                    <Typography gutterBottom variant="body2">
                      Your payment has been processed.
                    </Typography>
                    <Typography paragraph color="textSecondary">
                      We're setting up the student's account accounts. We'll
                      send instructions to <strong>{values.email}</strong>.
                    </Typography>
                    <Link to="/students">
                      <Button size="large" variant="contained" color="primary">
                        View Students
                      </Button>
                    </Link>
                  </Box>
                )}
              </Box>
            </CardContent>
            {step !== 6 && (
              <CardActions className={classes.actions}>
                <Button
                  size="large"
                  color="primary"
                  variant="outlined"
                  onClick={step === 1 ? () => history.goBack() : () => setStep(step - 1)}
                >
                  { step === 1 ? 'Cancel' : 'Back' }
                </Button>
                { step !== 5 && (
                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    onClick={onNext}
                  >
                    Next
                  </Button>
                )}
              </CardActions>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Purchase;
